import { useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { MIN_BRANDS_FOR_BENCHMARK, type Brand } from '@fametrue/shared';
import { MOBILE_QUERY, useMediaQuery } from '../lib/useMediaQuery';
import { Accordion } from './Accordion';
import { BrandHeader } from './BrandHeader';
import { MatrixChart } from './MatrixChart';
import { RankingSort, RankingTable, type RankingColumn } from './RankingTable';
import type { SectorSummary } from './api';
import { RISK_HEX, SIGNAL_QUALITY } from './palette';

/**
 * Level 3 — Intelligence, porting di `renderLevel3()`. Il riepilogo di settore
 * arriva da `/api/sectors/:key` (spec §10: `verticals` non si importa); sotto i
 * 3 brand nel verticale si mostra solo l'avviso.
 */
export function IntelligenceView({
  brand,
  sector,
}: {
  brand: Brand;
  sector: SectorSummary;
}): ReactNode {
  const { t } = useTranslation();
  const signal = SIGNAL_QUALITY[brand.signalQuality] ?? SIGNAL_QUALITY['partial']!;
  const [sortBy, setSortBy] = useState<RankingColumn>('fameWeb');
  // Matrice e "Posizionamento sul servizio" non si rendono sotto i 900 px, e al
  // loro posto va una riga che lo dice: vedi il commento alle rispettive
  // sezioni, in fondo al componente.
  const compact = useMediaQuery(MOBILE_QUERY);

  if (!sector.benchmarkAvailable || !sector.averages) {
    return (
      <>
        <BrandHeader brand={brand} badge={false} />
        <div className="level-placeholder">
          <div className="level-placeholder-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M18 20V10M12 20V4M6 20v-6" />
            </svg>
          </div>
          <h3>{t('intelligence.noBenchmark')}</h3>
          <p>
            {t('intelligence.noBenchmarkBody', {
              min: MIN_BRANDS_FOR_BENCHMARK,
              count: sector.brandCount,
              sector: sector.sector,
            })}
          </p>
        </div>
      </>
    );
  }

  const idx = brand.indices;
  const brands = sector.brands;
  const total = brands.length;

  const ftValues = brands.map((b) => b.fameIntegrity).filter((v): v is number => v !== null);

  /** Posizione per integrità: 1 è il migliore. Alimenta il blocco 02 della panoramica. */
  const ftPos = [...ftValues].sort((a, b) => b - a).indexOf(idx.fameIntegrity ?? 0) + 1;

  const nonConformi = ftValues.filter((v) => v < 50).length;
  const preCert = ftValues.filter((v) => v >= 50 && v < 80).length;
  const conformi = ftValues.filter((v) => v >= 80).length;
  const rischioRidotto = ftValues.filter((v) => v >= 50).length;

  return (
    <>
      <BrandHeader brand={brand} />

      <div className="sources-row">
        <span className="sources-label">{t('intelligence.reportLabel')}</span>
        <div className="signal-quality" style={{ color: signal.color }}>
          <div className={`sq-dot ${brand.signalQuality}`} />
          {t('insight.signalWithQuality', {
            quality: t(`signalQuality.${brand.signalQuality}`),
          })}
        </div>
      </div>

      {/* Griglia inline nel riferimento, qui una classe: sotto i 900 px va a
          colonna singola e la card finisce sopra i due accordion. */}
      <div className="l3-overview-split">
        <div className="l2-section" style={{ marginBottom: 0 }}>
          <div className="l2-section-title">{t('intelligence.sectorOverview')}</div>
          <div className="platform-card" style={{ padding: '16px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <OverviewNumber value={String(total)} label={t('intelligence.competitorsAnalysed')} />
              <div
                style={{
                  width: 1,
                  background: 'var(--border-soft)',
                  alignSelf: 'stretch',
                  margin: '6px 0',
                }}
              />
              <OverviewNumber
                value={String(rischioRidotto)}
                suffix={`/${total}`}
                color="#2A9D8F"
                label={t('intelligence.lowerRisk')}
              />
            </div>
          </div>
        </div>

        <div className="l2-section" style={{ marginBottom: 0 }}>
          {/* Titolo vuoto: allinea gli accordion alla card di sinistra, come il
              `&nbsp;` del riferimento. Il pallino arancione che
              `.l2-section-title::before` gli disegna comunque e' spento in
              app.css (divergenza dal v40), e a colonna singola, dove non
              allinea piu' nulla, sparisce l'intera riga. */}
          <div className="l2-section-title l3-title-spacer">&nbsp;</div>
          <Accordion num="01" title={t('intelligence.regulatoryContext')}>
            <div
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 14,
                color: 'var(--text-2)',
                lineHeight: 1.7,
              }}
            >
              <SectorInsight
                sector={sector.sector}
                total={total}
                nonConformi={nonConformi}
                preCert={preCert}
                conformi={conformi}
              />
            </div>
          </Accordion>
          <Accordion
            num="02"
            title={t('intelligence.positioning')}
            // Il "°" stava nel codice e in inglese dava "2° of 4" (feedback
            // 23/10, punto 9). Ora la frase è una chiave per lingua: l'italiano
            // tiene l'ordinale, come il corpo del riquadro ("al 2° posto su 4"),
            // l'inglese usa la forma senza suffisso della frase corrispondente
            // ("ranks 2 of 4").
            subtitle={t('intelligence.positionSubtitle', {
              name: brand.name,
              pos: ftPos,
              total,
            })}
          >
            <div
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 14,
                color: 'var(--text-2)',
                lineHeight: 1.7,
              }}
            >
              <Trans
                i18nKey={
                  (idx.fameIntegrity ?? 0) >= 50
                    ? 'intelligence.positionConforming'
                    : 'intelligence.positionNonConforming'
                }
                // `count` e non `lowerRisk`: i18next sceglie la forma singolare
                // o plurale solo su quel nome (feedback 23/10, punto 10). Con un
                // solo competitor a rischio ridotto si leggeva "Solo 1
                // competitor su 3 presentano", e in inglese "1 competitors".
                values={{ name: brand.name, pos: ftPos, total, count: rischioRidotto }}
                components={{ b: <b /> }}
              />
            </div>
          </Accordion>
        </div>
      </div>

      {/* La matrice non si rende sotto i 900 px. Non e' un `display: none`: e'
          uno scatter Chart.js con dodici brand, etichette anti-collisione
          disegnate a mano e assi ruotati con `writing-mode`, illeggibile sotto
          i ~600 px di larghezza utile. Non renderizzarla evita anche di
          istanziare il grafico e i suoi listener per nulla. */}
      {compact ? (
        /* Una riga sola al posto di entrambe le sezioni che restano fuori, qui
           dove sarebbe la prima: senza, la pagina su telefono finiva con la
           classifica e sembrava semplicemente più corta (feedback 23/10,
           punto 12). */
        <div className="l3-narrow-note">{t('intelligence.narrowScreenNote')}</div>
      ) : (
        <div className="l2-section">
          <div className="l2-section-title">{t('intelligence.positioningMatrix')}</div>
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                paddingRight: 8,
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 18,
                  fontWeight: 500,
                  letterSpacing: '-0.3px',
                }}
              >
                <span style={{ color: 'var(--navy)' }}>Fame</span>
                <span style={{ color: '#185FA5' }}>Integrity</span>
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 14,
                  color: 'var(--text-3)',
                  marginTop: 4,
                }}
              >
                {t('intelligence.axisIntegrity')}
              </div>
            </div>
            <div style={{ flex: 1, position: 'relative' }}>
              <div style={{ position: 'relative', width: '100%', height: 420 }}>
                <MatrixChart brands={brands} currentSlug={brand.id} />
              </div>
              <div style={{ textAlign: 'center', paddingTop: 4 }}>
                <div
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 18,
                    fontWeight: 500,
                    letterSpacing: '-0.3px',
                  }}
                >
                  <span style={{ color: 'var(--navy)' }}>Fame</span>
                  <span style={{ color: '#DF6616' }}>Index</span>
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 14,
                    color: 'var(--text-3)',
                    marginTop: 2,
                  }}
                >
                  {t('intelligence.axisIndex')}
                </div>
              </div>
            </div>
          </div>
          <MatrixLegend />
        </div>
      )}

      <div className="l2-section">
        <div className="l2-section-title">{t('intelligence.ranking')}</div>
        <Accordion
          num="B1"
          title={t('intelligence.fullRanking')}
          subtitle={<RankingSort value={sortBy} onChange={setSortBy} />}
        >
          <RankingTable brands={brands} currentSlug={brand.id} sortBy={sortBy} />
        </Accordion>
      </div>

      {/* Anche questa resta fuori sotto i 900 px: il corpo e' `level3.extra`,
          markup del committente con SVG e larghezze fisse, che non si adatta e
          non e' nostro da riscrivere. L'avviso e' uno solo e le nomina
          entrambe: sta piu' sopra, dove sarebbe la matrice. */}
      {compact ? null : <ServicePositioning brand={brand} />}

      <CustomReportCard />
    </>
  );
}

/**
 * Sezione "Posizionamento sul servizio", introdotta dal v40 fra la classifica e
 * il blocco commerciale.
 *
 * Il corpo arriva da `level3.extra`: un blocco di markup del committente (nel
 * dataset, un grafico di confronto competitor costruito con SVG e stili inline)
 * che si rende tale e quale, come già `analysis`. Quando manca, il riferimento
 * mostra un testo di rinvio invece di nascondere la sezione.
 */
function ServicePositioning({ brand }: { brand: Brand }): ReactNode {
  const { t } = useTranslation();
  const extra = brand.level3?.extra;

  return (
    <div className="l2-section">
      <div className="l2-section-title">{t('intelligence.servicePositioning')}</div>
      <Accordion
        num="C1"
        title={t('intelligence.complaintsTitle')}
        subtitle={t(extra ? 'intelligence.complaintsSubtitle' : 'intelligence.complaintsOnRequest')}
      >
        {extra ? (
          <div dangerouslySetInnerHTML={{ __html: extra }} />
        ) : (
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              color: 'var(--text-2)',
              lineHeight: 1.75,
              maxWidth: 760,
            }}
          >
            {t('intelligence.complaintsPlaceholder')}
          </div>
        )}
      </Accordion>
    </div>
  );
}

function OverviewNumber({
  value,
  suffix,
  color,
  label,
}: {
  value: string;
  suffix?: string;
  color?: string;
  label: string;
}): ReactNode {
  return (
    <div style={{ flex: 1, textAlign: 'center', padding: 8 }}>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 34,
          fontWeight: 400,
          color: color ?? 'var(--ink)',
          letterSpacing: '-1.5px',
        }}
      >
        {value}
        {suffix ? (
          <span style={{ fontSize: 16, color: 'var(--text-3)', opacity: 0.5 }}>{suffix}</span>
        ) : null}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 11,
          color: 'var(--text-3)',
          marginTop: 4,
        }}
      >
        {label}
      </div>
    </div>
  );
}

/** Il testo cambia in base a come è distribuito il settore, come nel riferimento. */
function SectorInsight({
  sector,
  total,
  nonConformi,
  preCert,
  conformi,
}: {
  sector: string;
  total: number;
  nonConformi: number;
  preCert: number;
  conformi: number;
}): ReactNode {
  const components = { em: <em />, b: <b /> };

  if (nonConformi / total >= 0.5) {
    return (
      <Trans
        i18nKey="intelligence.insightMostlyNonCompliant"
        values={{ sector, count: nonConformi, total }}
        components={components}
      />
    );
  }
  if (conformi / total >= 0.5) {
    return (
      <Trans
        i18nKey="intelligence.insightMostlyCompliant"
        values={{ count: conformi, total }}
        components={components}
      />
    );
  }
  if (preCert / total >= 0.5) {
    return (
      <Trans
        i18nKey="intelligence.insightMostlyPreCert"
        values={{ count: preCert, total }}
        components={components}
      />
    );
  }
  return (
    <Trans
      i18nKey="intelligence.insightFragmented"
      values={{ nonConformi, preCert, conformi }}
      components={components}
    />
  );
}

function MatrixLegend(): ReactNode {
  const { t } = useTranslation();
  const dot = (style: React.CSSProperties): ReactNode => <span style={style} />;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 12,
        fontFamily: 'var(--font-sans)',
        fontSize: 11,
        color: 'var(--text-3)',
      }}
    >
      {[1, 2, 3, 4, 5].map((level) => (
        <span key={level} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {dot({ width: 8, height: 8, borderRadius: '50%', background: RISK_HEX[level] })}
          {t('intelligence.legendRisk', { level })}
        </span>
      ))}
      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {dot({
          width: 8,
          height: 8,
          borderRadius: '50%',
          border: '2px solid #888',
          boxSizing: 'border-box',
          background: 'transparent',
        })}
        {t('intelligence.legendIndexNd', { nd: t('integrity.nd') })}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {dot({
          width: 10,
          height: 10,
          borderRadius: '50%',
          border: '2.5px solid var(--navy)',
          boxSizing: 'border-box',
          background: 'transparent',
        })}
        {t('intelligence.currentBrand')}
      </span>
    </div>
  );
}

/**
 * Recapito del blocco commerciale. Resta letterale e fuori dai file di
 * localizzazione: è un indirizzo, non una stringa di interfaccia.
 *
 * L'oggetto della mail, invece, è una stringa di interfaccia: era una costante
 * italiana e arrivava in italiano anche a chi usa la versione inglese
 * (16/10, Level 3 punto 3). Ora viene da `intelligence.customReportSubject`.
 */
const CUSTOM_REPORT_MAIL = 'cert@fametrue.com';

/** Blocco commerciale sul report personalizzato: contenuto fisso del riferimento. */
function CustomReportCard(): ReactNode {
  const { t } = useTranslation();
  const chips = ['chipProfile', 'chipComparison', 'chipGap', 'chipBestPractice'] as const;

  return (
    <div className="l2-section">
      <div className="l2-section-title">{t('intelligence.customReport')}</div>
      <div
        style={{
          background: 'linear-gradient(135deg, var(--b2-vivo-a) 0%, var(--b2-vivo-b) 100%)',
          borderRadius: 12,
          padding: '24px 28px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            fontFamily: 'var(--font-display)',
            fontSize: 80,
            fontWeight: 500,
            letterSpacing: '-2px',
            opacity: 0.04,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            color: '#fff',
          }}
        >
          Intelligence
        </div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 20,
              fontWeight: 500,
              color: '#fff',
              marginBottom: 10,
            }}
          >
            {t('intelligence.customReportTitle')}
          </div>
          <div
            className="spot-desc"
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 13.5,
              color: 'rgba(255,255,255,0.88)',
              lineHeight: 1.6,
              marginBottom: 18,
              whiteSpace: 'nowrap',
            }}
          >
            {t('intelligence.customReportBody')}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
            {chips.map((chip) => (
              <span
                key={chip}
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.92)',
                  background: 'rgba(255,255,255,0.14)',
                  border: '1px solid rgba(255,255,255,0.28)',
                  borderRadius: 3,
                  padding: '4px 10px',
                }}
              >
                {t(`intelligence.${chip}`)}
              </span>
            ))}
          </div>
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              color: 'rgba(255,255,255,0.9)',
              lineHeight: 1.6,
            }}
          >
            <Trans
              i18nKey="intelligence.customReportCta"
              components={{
                mail: (
                  <a
                    href={`mailto:${CUSTOM_REPORT_MAIL}?subject=${encodeURIComponent(
                      t('intelligence.customReportSubject'),
                    )}`}
                    style={{
                      color: '#fff',
                      fontWeight: 600,
                      textDecoration: 'underline',
                      textUnderlineOffset: 3,
                    }}
                  />
                ),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
